import {
  buildN8nInventoryUrls,
  getN8nOrigin,
  type N8nInventoryOperation,
} from "./n8nWebhookConfig";

type FetchOptions = {
  method: "GET" | "POST" | "PUT" | "DELETE";
  endpoint?: string;
  operation?: N8nInventoryOperation;
  variables?: Record<string, unknown>;
  onError?: (error: Error) => void;
};

type N8nEnvelope<T = unknown> = {
  success?: boolean;
  data?: T;
  error?: string;
  operationId?: string;
};

const NETWORK_RETRY_ATTEMPTS = 3;
const NETWORK_RETRY_BASE_MS = 400;

export const clearInventorySession = (): void => {
  sessionStorage.removeItem("inventoryToken");
};

const getToken = (): string => {
  const storedToken = sessionStorage.getItem("inventoryToken");
  if (storedToken) return storedToken;

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token") || "";
  if (token) {
    sessionStorage.setItem("inventoryToken", token);
  }
  return token;
};

const getLegacyBaseUrl = (): string | undefined => {
  const url = import.meta.env.VITE_API_URL;
  if (!url || typeof url !== "string") return undefined;
  return url.replace(/\/$/, "");
};

const resolveRequestUrls = (
  endpoint: string,
  operation?: N8nInventoryOperation
): string[] => {
  const n8nOrigin = getN8nOrigin();

  if (n8nOrigin) {
    if (!operation) {
      throw new Error(
        `Missing n8n operation for endpoint ${endpoint}. Pass operation in inventoryFetchClient options.`
      );
    }
    return buildN8nInventoryUrls(operation, endpoint);
  }

  const legacyBase = getLegacyBaseUrl();
  if (legacyBase) {
    return [`${legacyBase}${endpoint}`];
  }

  throw new Error(
    "Set VITE_N8N_ORIGIN (n8n on Render) or VITE_API_URL (local server-test)"
  );
};

const parseResponseBody = async (response: Response): Promise<unknown> => {
  if (response.status === 204) {
    return {};
  }

  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON response (HTTP ${response.status})`);
  }
};

const formatApiErrorMessage = (
  result: unknown,
  status: number,
  statusText: string
): string => {
  if (result && typeof result === "object" && !Array.isArray(result)) {
    const envelope = result as N8nEnvelope & {
      details?: Array<{ message?: string }>;
    };
    if (typeof envelope.error === "string" && envelope.error.length > 0) {
      const detail = envelope.details?.[0]?.message;
      return detail ? `${envelope.error}: ${detail}` : envelope.error;
    }
  }
  return `HTTP ${status}: ${statusText}`;
};

const unwrapN8nResponse = <T>(result: unknown, status: number): T => {
  if (result && typeof result === "object" && !Array.isArray(result)) {
    const envelope = result as N8nEnvelope<T> & Record<string, unknown>;

    if (envelope.success === false) {
      const message =
        typeof envelope.error === "string" ? envelope.error : "Unknown error";
      throw new Error(message);
    }

    if (typeof envelope.error === "string" && envelope.error.length > 0) {
      throw new Error(envelope.error);
    }

    if (envelope.success === true && "data" in envelope) {
      if (import.meta.env.DEV && envelope.operationId) {
        console.debug(`[inventoryApi] ${envelope.operationId}`);
      }
      return envelope.data as T;
    }

    if ("data" in envelope && envelope.data !== undefined) {
      return envelope.data as T;
    }
  }

  if (!responseOk(status)) {
    throw new Error(`HTTP ${status}`);
  }

  return result as T;
};

const responseOk = (status: number): boolean => status >= 200 && status < 300;

export const isNetworkFetchError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    error.name === "TypeError" ||
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("load failed") ||
    message.includes("network request failed")
  );
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const buildFetchInit = (
  method: FetchOptions["method"],
  variables?: Record<string, unknown>
): RequestInit => {
  const token = getToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const fetchOptions: RequestInit = { method, headers };
  if (method !== "GET" && variables) {
    fetchOptions.body = JSON.stringify(variables);
  }
  return fetchOptions;
};

const fetchOnce = async (url: string, init: RequestInit): Promise<Response> => {
  try {
    return await fetch(url, init);
  } catch (networkError) {
    throw networkError instanceof Error
      ? networkError
      : new Error("Failed to fetch");
  }
};

export const inventoryFetchClient = async <T>(
  endpoint: string,
  options: FetchOptions
): Promise<T> => {
  const { method, variables, onError, operation } = options;
  const urls = resolveRequestUrls(endpoint, operation);
  const init = buildFetchInit(method, variables);

  let lastError: Error | null = null;

  for (let urlIndex = 0; urlIndex < urls.length; urlIndex += 1) {
    const url = urls[urlIndex];
    const retries = urlIndex === 0 ? NETWORK_RETRY_ATTEMPTS : 1;

    for (let attempt = 1; attempt <= retries; attempt += 1) {
      try {
        const response = await fetchOnce(url, init);
        const result = await parseResponseBody(response);

        if (response.status === 401) {
          clearInventorySession();
          let message =
            "Session expired or invalid. Please reload from the parent application.";
          if (
            result &&
            typeof result === "object" &&
            "error" in result &&
            typeof (result as N8nEnvelope).error === "string"
          ) {
            message = (result as N8nEnvelope).error as string;
          }
          const error = new Error(message);
          onError?.(error);
          throw error;
        }

        if (response.status === 404 && urlIndex < urls.length - 1) {
          lastError = new Error(
            formatApiErrorMessage(result, response.status, response.statusText)
          );
          break;
        }

        if (!responseOk(response.status)) {
          try {
            unwrapN8nResponse(result, response.status);
          } catch (parsedError) {
            const error =
              parsedError instanceof Error
                ? parsedError
                : new Error(
                    formatApiErrorMessage(
                      result,
                      response.status,
                      response.statusText
                    )
                  );
            onError?.(error);
            throw error;
          }
          const error = new Error(
            formatApiErrorMessage(result, response.status, response.statusText)
          );
          onError?.(error);
          throw error;
        }

        try {
          return unwrapN8nResponse<T>(result, response.status);
        } catch (error) {
          const err = error instanceof Error ? error : new Error("Unknown error");
          onError?.(err);
          throw err;
        }
      } catch (error) {
        lastError = error instanceof Error ? error : new Error("Failed to fetch");
        if (!isNetworkFetchError(lastError)) {
          throw lastError;
        }
        if (attempt < retries) {
          await wait(NETWORK_RETRY_BASE_MS * attempt);
        }
      }
    }
  }

  const failed =
    lastError ?? new Error("Failed to fetch");
  if (isNetworkFetchError(failed)) {
    const wrapped = new Error(
      "Failed to fetch: n8n no respondió. Revisá la URL del webhook (ítems/fotos) y CORS de Wasabi si hay fotos."
    );
    onError?.(wrapped);
    throw wrapped;
  }
  onError?.(failed);
  throw failed;
};
